import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import {
    Button,
    Carousel,
    CarouselCard,
    CarouselNav,
    CarouselNavButton,
    CarouselSlider,
    CarouselViewport,
    Image,
    makeStyles,
    mergeClasses,
    shorthands,
    Text,
    tokens,
    typographyStyles,
} from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { useApplicationStyles } from '../App'
import useViewport, { Viewport } from '../../hooks/useViewport'
import useResolvedTheme from '../../hooks/useResolvedTheme'

const useStyles = makeStyles({
    wrapper: {
        justifyContent: 'center',
        display: 'flex',
    },
    container: {
        flexGrow: 1,
        padding: tokens.spacingVerticalL,
    },
    carousel: {
        maxWidth: '700px',
    },
    image: {
        width: '100%',
    },
    footer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: 'auto',
        ...shorthands.gap(tokens.spacingHorizontalM),
        marginTop: tokens.spacingVerticalL,
        marginBottom: tokens.spacingVerticalL,
    },
    footerTiny: {
        flexDirection: 'column',
        width: 'unset',
    },
    header: {
        display: 'block',
        // We use margin instead of padding to avoid messing with the focus indicator in the header
        margin: tokens.spacingVerticalXXL,
        marginBottom: tokens.spacingVerticalS,
        ...typographyStyles.subtitle1,
    },
    text: {
        display: 'block',
        margin: `${tokens.spacingVerticalS} ${tokens.spacingVerticalXXL}`,
        ...typographyStyles.body1,
    },
})

export default function FirstVisit(props: { setVisited: () => void }): ReactElement {
    const appStyles = useApplicationStyles()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()
    const { resolvedTheme } = useResolvedTheme()

    const [activeIndex, setActiveIndex] = useState(0)

    const viewportTiny = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    const setPage = (page: number) => {
        if (page >= 3) {
            props.setVisited()
        } else {
            setActiveIndex(page)
        }
    }

    return (
        <div className={mergeClasses(appStyles.wrapper, resolvedTheme === 'dark' && appStyles.wrapperDark, styles.wrapper)}>
            <div className={mergeClasses(appStyles.container, styles.container)}>
                <Carousel
                    className={styles.carousel}
                    groupSize={1}
                    circular
                    activeIndex={activeIndex}
                    motion="fade"
                    onActiveIndexChange={(_e, data) => setActiveIndex(data.index)}
                >
                    <CarouselViewport>
                        <CarouselSlider>
                            <CarouselCard key={0}>
                                <Image src={'./images/firstvisit/image1.svg'} className={styles.image} alt={'HAMMOCK logo'} />
                                <h1 tabIndex={-1} className={styles.header}>
                                    {translate('firstvisit.card1.title')}
                                </h1>
                                <div
                                    className={styles.text}
                                    dangerouslySetInnerHTML={{
                                        __html: translate('firstvisit.card1.description'),
                                    }}
                                ></div>
                            </CarouselCard>
                            <CarouselCard key={1}>
                                <Image src={'./images/firstvisit/image2.gif'} className={styles.image} alt={'Design'} />
                                <h1 tabIndex={-1} className={styles.header}>
                                    {translate('firstvisit.card2.title')}
                                </h1>
                                <Text className={styles.text}>{translate('firstvisit.card2.description')}</Text>
                            </CarouselCard>

                            <CarouselCard key={2}>
                                <Image src={'./images/firstvisit/image3.png'} className={styles.image} alt={'E-mail'} />
                                <h1 tabIndex={-1} className={styles.header}>
                                    {translate('firstvisit.card3.title')}
                                </h1>
                                <Text className={styles.text}>{translate('firstvisit.card3.description')}</Text>
                            </CarouselCard>
                        </CarouselSlider>
                    </CarouselViewport>
                    <div className={mergeClasses(styles.footer, viewportTiny && styles.footerTiny)}>
                        <Button appearance="primary" onClick={() => setPage(activeIndex - 1)} disabled={activeIndex === 0}>
                            {translate('firstvisit.previous')}
                        </Button>
                        <CarouselNav appearance="brand">{index => <CarouselNavButton aria-label={`Carousel Nav Button ${index}`} />}</CarouselNav>

                        <Button appearance="primary" onClick={() => setPage(activeIndex + 1)}>
                            {activeIndex < 2 ? translate('firstvisit.next') : translate('firstvisit.done')}
                        </Button>
                    </div>
                    <div className={mergeClasses(styles.footer, viewportTiny && styles.footerTiny)}>
                        <Button onClick={() => props.setVisited()}>{translate('firstvisit.skip')}</Button>
                    </div>
                </Carousel>
            </div>
        </div>
    )
}
